import { useState } from 'react';
import type { ReactNode } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useGetLatestPluginReleaseQuery } from '../../../api/plugins.api';
import { authClient } from '../../../auth/authClient';
import { CommandBlock } from '../../../components/CommandBlock';
import { ContentSection } from '../../../components/ContentSection';
import { ContentState } from '../../../components/ContentState';
import { config } from '../../../config';
import { cardSurfaceSx, contentWidthSx, pageGutterSx, sectionSpacingSx } from '../../../theme';
import { FormSwitch } from './FormSwitch';
import { InstallRoutes } from './InstallRoutes';
import {
  COMMAND_FORMS,
  DOWNLOADS_DIR,
  MARKETPLACE_DIR,
  MARKETPLACE_NAME,
  ORG_MARKETPLACE_NAME,
  pluginFileName,
} from './constants';
import type { CommandForm } from './constants';

const Problem = ({ symptom, cause, fix }: { symptom: string; cause: ReactNode; fix: ReactNode }) => (
  <Box
    component="li"
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 2fr) minmax(0, 3fr)' },
      gap: { xs: 6, md: 8 },
      borderTop: 1,
      borderColor: 'divider',
      paddingTop: 6,
    }}
  >
    <Stack spacing={3}>
      <Typography variant="h4" component="h3">
        {symptom}
      </Typography>
      <Typography variant="body2">{cause}</Typography>
    </Stack>
    <Stack spacing={3}>{fix}</Stack>
  </Box>
);

/**
 * Downloads the current release via the JWT-gated route and saves it through
 * an object-URL anchor click, the one way a header-authenticated file reaches
 * disk from a browser. Default redirect-follow, unlike a raw `fetch` probe of
 * this route: the bucket now carries a matching CORS rule (see
 * `services/plugin-artifacts/application.yml`), so the browser can read the S3
 * response instead of blocking it as cross-origin.
 */
const downloadLatestRelease = async (version: string): Promise<void> => {
  const token = await authClient.getAccessToken();
  const headers: Record<string, string> = {};
  if (token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(
    new Request(`${config.apiBaseUrl}/plugins/latest/download`, { headers }),
  );
  if (!response.ok) {
    throw new Error(`Download failed with status ${String(response.status)}`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = pluginFileName(version);
  link.click();
  URL.revokeObjectURL(objectUrl);
};

/**
 * Moving from one install route to the other. Removing a marketplace
 * uninstalls every plugin from it, so each direction is a remove and then the
 * other column's steps. No Claude Desktop form: it only ever has the claude.ai
 * route, so there is nothing to move between.
 */
const SwitchingRoutes = () => {
  const [form, setForm] = useState<CommandForm>('shell');
  const remove = (name: string) =>
    form === 'shell' ? `claude plugin marketplace remove ${name}` : `/plugin marketplace remove ${name}`;
  const deleteFolder = `rm -rf ${MARKETPLACE_DIR}/${MARKETPLACE_NAME}`;

  return (
    <ContentSection
      heading="Switching routes"
      caption="Moving an existing install from one route to the other."
    >
      <Box sx={{ ...cardSurfaceSx, padding: 6 }}>
        <Stack spacing={6}>
          <FormSwitch
            label="Switching routes command form"
            options={COMMAND_FORMS}
            value={form}
            onChange={setForm}
          />

          <Stack spacing={3}>
            <Typography variant="h4" component="h3">
              Augment Toolkit to Claude.ai Marketplace
            </Typography>
            <Typography variant="body2">
              Remove the zip marketplace — that uninstalls <code>and-core</code> and{' '}
              <code>luna-telemetry</code> with it — and delete the unzipped folder:
            </Typography>
            <CommandBlock>
              {form === 'shell'
                ? `${remove(MARKETPLACE_NAME)}\n${deleteFolder}`
                : remove(MARKETPLACE_NAME)}
            </CommandBlock>
            {form === 'claude' && (
              <>
                <Typography variant="body2">Then, from a terminal:</Typography>
                <CommandBlock>{deleteFolder}</CommandBlock>
              </>
            )}
            <Typography variant="body2">
              Then follow the Claude.ai Marketplace steps above.
            </Typography>
          </Stack>

          <Stack spacing={3}>
            <Typography variant="h4" component="h3">
              Claude.ai Marketplace to Augment Toolkit
            </Typography>
            <Typography variant="body2">
              Remove the claude.ai marketplace — that uninstalls <code>luna-telemetry</code> with
              it:
            </Typography>
            <CommandBlock>{remove(ORG_MARKETPLACE_NAME)}</CommandBlock>
            <Typography variant="body2">
              Then follow the Augment Toolkit steps above.
            </Typography>
          </Stack>

          <Typography variant="body2" sx={{ color: 'text.meta' }}>
            Directories you have already bound stay bound — the bind lives in the directory, not
            in the plugin. Run <code>/reload-plugins</code> or restart Claude Code once the new
            install is done.
          </Typography>
        </Stack>
      </Box>
    </ContentSection>
  );
};

/** `/toolkit/luna-telemetry` — the Claude Code plugin that attributes AI spend. */
export const LunaTelemetryPage = () => {
  const { data, isLoading, error, refetch } = useGetLatestPluginReleaseQuery();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string>();

  const download = () => {
    if (data === undefined) {
      return;
    }

    setIsDownloading(true);
    setDownloadError(undefined);
    downloadLatestRelease(data.version)
      .catch((downloadCatchError: unknown) => {
        setDownloadError(
          downloadCatchError instanceof Error
            ? downloadCatchError.message
            : 'The download failed.',
        );
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  const releaseCard = data !== undefined && (
      <Box sx={{ ...cardSurfaceSx, padding: 6 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={4}
          sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
        >
          <Stack spacing={1}>
            <Typography variant="h4" component="h2">
              Version {data.version}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.meta' }}>
              Released {new Date(data.released).toLocaleDateString()}
            </Typography>
          </Stack>

          <Box sx={{ flexShrink: 0 }}>
            <Button variant="contained" onClick={download} disabled={isDownloading}>
              {isDownloading ? 'Downloading…' : 'Download marketplace'}
            </Button>
          </Box>
        </Stack>

        {downloadError !== undefined && (
          <Typography variant="body2" color="error" sx={{ marginTop: 3 }}>
            {downloadError}
          </Typography>
        )}
      </Box>
  );

  return (
    <Box sx={pageGutterSx}>
      <Box sx={{ ...contentWidthSx, py: 12 }}>
        <Typography variant="h1" component="h1" gutterBottom>
          Luna Telemetry
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 6 }}>
          The Claude Code plugin to track and attribute costs to Luna engagements.
        </Typography>

        <ContentState
          isLoading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          errorTitle="Unable to load the latest release"
          errorNoun="release"
        >
          {data !== undefined && (
            <>
              <Box sx={sectionSpacingSx}>
                <ContentSection heading="What it does">
                  <Stack spacing={3}>
                    <Typography variant="body2">
                      It links a directory on your machine to a Luna engagement you are
                      allocated to. From then on, every Claude Code session you run in that
                      directory reports its token usage against that engagement, so AI spend
                      lands on the right project instead of one unattributed pile.
                    </Typography>
                    <Typography variant="body2">
                      Linking is a one-off per directory. <code>/bind</code> signs you in over
                      SSO and mints a token for your engagement — picked automatically if you
                      are allocated to just one, or chosen by you if you are allocated to
                      several — and writes it into the directory. Nothing else about your
                      sessions changes.
                    </Typography>
                  </Stack>
                </ContentSection>
              </Box>

              <Box sx={sectionSpacingSx}>
                <InstallRoutes version={data.version} releaseCard={releaseCard} />
              </Box>

              <Box sx={sectionSpacingSx}>
                <SwitchingRoutes />
              </Box>

              <Box sx={sectionSpacingSx}>
                <ContentSection
                  heading="Commands"
                  caption="What you can run once it is installed."
                >
                  <Stack spacing={6}>
                    <Stack spacing={3}>
                      <Typography variant="h4" component="h3">
                        /bind
                      </Typography>
                      <Typography variant="body2">
                        Run <code>/bind</code> in the directory you want attributed. It signs
                        you in over SSO, finds the Claude-enabled engagements you are allocated
                        to, and mints a token for the one you choose. Allocated to one, it picks
                        that one; allocated to several, it asks; allocated to none, it says so
                        and stops.
                      </Typography>
                      <CommandBlock>/bind</CommandBlock>

                      <Typography variant="body2">
                        Point it at a specific engagement:
                      </Typography>
                      <CommandBlock>{'/bind --engagement <engagement-id>'}</CommandBlock>

                      <Typography variant="body2">
                        Remove the link — do this before handing a codebase to a client:
                      </Typography>
                      <CommandBlock>/bind --clear</CommandBlock>
                    </Stack>

                    <Box
                      role="note"
                      sx={{
                        ...cardSurfaceSx,
                        backgroundColor: 'accent.infoSurface',
                        borderColor: 'accent.infoBorder',
                        boxShadow: 2,
                        padding: 6,
                      }}
                    >
                      <Typography
                        variant="overline"
                        component="p"
                        sx={{ color: 'accent.info', letterSpacing: '1px' }}
                      >
                        Files /bind writes
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        <code>/bind</code> writes credentials into the directory, readable only
                        by you, and adds each one to an ignore file itself. Nothing to do by
                        hand:
                      </Typography>

                      <Box sx={{ mt: 3 }}>
                        <CommandBlock>
                          {'.luna-ai-usage.json\n.luna-bind-session.json'}
                        </CommandBlock>
                      </Box>

                      <Typography variant="body2" sx={{ mt: 3 }}>
                        <code>.luna-ai-usage.json</code> is the bind itself and holds the
                        engagement token, so its entry goes in the repository&rsquo;s{' '}
                        <code>.gitignore</code>, where you can see it. Expect a one-line diff
                        there after your first <code>/bind</code> — an ignore nobody can see is
                        how a live token ends up somewhere it should not be.
                      </Typography>

                      <Typography variant="body2" sx={{ mt: 3, color: 'text.meta' }}>
                        <code>.luna-bind-session.json</code> is a short-lived sign-in cache{' '}
                        <code>/bind</code> removes once it succeeds, and leaves behind if a run
                        stops partway. That one goes to <code>.git/info/exclude</code> instead —
                        it is gone too quickly to be worth a line the repository tracks.
                      </Typography>
                    </Box>

                    <Stack spacing={3}>
                      <Typography variant="h4" component="h3">
                        What gets reported
                      </Typography>
                      <Typography variant="body2">
                        <code>luna-telemetry</code>&rsquo;s hooks report token usage for
                        directories you have bound, and do nothing anywhere else. A report goes
                        to Luna:
                      </Typography>
                      <Box
                        component="ul"
                        sx={{ margin: 0, paddingLeft: 5, typography: 'body2', '& li + li': { mt: 1 } }}
                      >
                        <li>
                          at the end of a turn, once the session has used another 25,000 tokens
                          since its last report
                        </li>
                        <li>
                          when the session ends — <code>/exit</code>, <code>/clear</code>, or
                          closing Claude Code.
                        </li>
                      </Box>
                      <Typography variant="body2">
                        Each report carries the session ID, the token counts per day for each
                        model — input, output, cached and cache writes — and a timestamp,
                        authenticated with the engagement token <code>/bind</code> minted.{' '}
                        <strong>
                          Never your prompts, Claude&rsquo;s responses, your code, file names or
                          anything personal.
                        </strong>
                      </Typography>
                    </Stack>
                  </Stack>
                </ContentSection>
              </Box>

              <Box sx={sectionSpacingSx}>
                <ContentSection
                  heading="Troubleshooting"
                  caption="What went wrong, then the fix."
                >
                  <Stack
                    component="ul"
                    spacing={6}
                    sx={{ listStyle: 'none', margin: 0, padding: 0 }}
                  >
                    <Problem
                      symptom="The terminal cannot open your Downloads folder"
                      cause={
                        <>
                          <code>unzip</code> fails with <code>Operation not permitted</code>, and
                          so does <code>ls {DOWNLOADS_DIR}</code>. macOS is refusing your terminal
                          access to Downloads, and <code>sudo</code> does not get round it.
                        </>
                      }
                      fix={
                        <>
                          <Typography variant="body2">
                            Drag the zip from Downloads to your home folder in Finder, then unzip it
                            from there:
                          </Typography>
                          <CommandBlock>
                            {`unzip ~/${pluginFileName(data.version)} -d ${MARKETPLACE_DIR}/`}
                          </CommandBlock>
                        </>
                      }
                    />

                    <Problem
                      symptom="Usage never reaches your engagement"
                      cause={
                        <>
                          Every hook in both plugins runs <code>python3</code> from your PATH.
                          When that is missing or broken the hooks fail and the session carries
                          on without them, so nothing is reported — and in a directory you have
                          not bound, the reminder to run <code>/bind</code> never appears either.
                        </>
                      }
                      fix={
                        <>
                          <Typography variant="body2">
                            Check it in a terminal. If it prints no version, or macOS offers to
                            install the command line developer tools, install Python 3 so this
                            works in a new terminal, then restart Claude Code:
                          </Typography>
                          <CommandBlock>python3 --version</CommandBlock>
                          <Typography variant="body2" sx={{ color: 'text.meta' }}>
                            Pointing the plugin at a different interpreter does not last —{' '}
                            <code>/and-update</code> replaces the plugin files, and your edit with
                            them.
                          </Typography>
                        </>
                      }
                    />

                    <Problem
                      symptom="The plugins are installed but Claude Code does not know them"
                      cause={
                        <>
                          <code>claude plugin list</code> shows both plugins, but a session that
                          was already open does not know <code>/bind</code> or{' '}
                          <code>/and-update</code>. Installing from the shell takes effect in the
                          next session Claude Code starts, not one already running.
                        </>
                      }
                      fix={
                        <>
                          <Typography variant="body2">
                            Reload plugins in the open session, or restart Claude Code:
                          </Typography>
                          <CommandBlock>/reload-plugins</CommandBlock>
                        </>
                      }
                    />
                  </Stack>
                </ContentSection>
              </Box>
            </>
          )}
        </ContentState>
      </Box>
    </Box>
  );
};
